import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, RefreshCw, Clock, CreditCard, AlertCircle, CheckCircle, HelpCircle, Mail } from 'lucide-react';

const refundCases = [
  {
    title: 'Full Refund (Within 7 Days)',
    icon: CheckCircle,
    color: 'emerald',
    items: [
      'Request made within 7 days of purchase',
      'Less than 10 AI-generated content items used',
      'No quiz submissions during the trial period',
      'Technical issues preventing app usage'
    ]
  },
  {
    title: 'Partial Refund (8-30 Days)',
    icon: RefreshCw,
    color: 'amber',
    items: [
      'Pro-rated refund based on remaining subscription',
      'Applicable if significant technical issues occurred',
      'Reviewed on a case-by-case basis',
      'Processing may take 7-14 business days'
    ]
  },
  {
    title: 'No Refund Applicable',
    icon: AlertCircle,
    color: 'red',
    items: [
      'Request made after 30 days of purchase',
      'Extensive usage of AI features (50+ items generated)',
      'Account suspended due to policy violations',
      'Promotional or discounted purchases (unless required by law)'
    ]
  }
];

const processSteps = [
  { step: 1, title: 'Submit Request', desc: 'Email us at refunds@studyclaw.io with your registered email and order details' },
  { step: 2, title: 'Review', desc: 'Our team reviews your request within 2-3 business days' },
  { step: 3, title: 'Decision', desc: 'You receive an email with the refund decision and any next steps' },
  { step: 4, title: 'Processing', desc: 'Approved refunds are processed to your original payment method within 7-14 days' },
];

export default function RefundPolicyPage({ onBack }) {
  return (
    <div className="min-h-screen bg-[#0f172a] pb-16">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button 
          data-testid="back-from-refund" 
          onClick={onBack} 
          className="text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-white font-['Outfit']">Refund Policy</h1>
      </div>

      <div className="px-4 max-w-2xl mx-auto space-y-6">
        {/* Intro */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Our Refund Policy</h2>
              <p className="text-xs text-slate-500">Last updated: February 2026</p>
            </div>
          </div>
          <p className="text-sm text-slate-400">
            We want you to be completely satisfied with StudyClaw. If for any reason you're not happy with your 
            subscription, we offer a fair refund policy. Please read the terms below carefully.
          </p>
        </motion.div>

        {/* 7-Day Guarantee Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-emerald-600/20 to-cyan-600/20 border border-emerald-500/30 rounded-2xl p-6 text-center"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <Clock className="w-6 h-6 text-emerald-400" />
            <h3 className="text-lg font-bold text-white">7-Day Money-Back Guarantee</h3>
          </div>
          <p className="text-sm text-slate-300">
            Try StudyClaw risk-free! If you're not satisfied within the first 7 days, 
            get a full refund - no questions asked.
          </p>
        </motion.div>

        {/* Refund Cases */}
        {refundCases.map((refundCase, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (i + 2) * 0.1 }}
            className={`bg-slate-900/50 border rounded-2xl p-6 ${
              refundCase.color === 'emerald' ? 'border-emerald-500/20' :
              refundCase.color === 'amber' ? 'border-amber-500/20' :
              'border-red-500/20'
            }`}
          >
            <h3 className={`text-base font-bold mb-4 flex items-center gap-2 ${
              refundCase.color === 'emerald' ? 'text-emerald-400' :
              refundCase.color === 'amber' ? 'text-amber-400' :
              'text-red-400'
            }`}>
              <refundCase.icon className="w-5 h-5" />
              {refundCase.title}
            </h3>
            <ul className="space-y-2">
              {refundCase.items.map((item, j) => (
                <li key={j} className="text-sm text-slate-400 flex items-start gap-2">
                  <span className={`mt-1 ${
                    refundCase.color === 'emerald' ? 'text-emerald-400' :
                    refundCase.color === 'amber' ? 'text-amber-400' :
                    'text-red-400'
                  }`}>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}

        {/* Refund Process */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
        >
          <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-violet-400" />
            How to Request a Refund
          </h3>
          <div className="space-y-4">
            {processSteps.map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-violet-400">{step.step}</span>
                </div>
                <div className="flex-1 pb-4 border-b border-slate-800 last:border-0">
                  <h4 className="text-sm font-semibold text-white mb-1">{step.title}</h4>
                  <p className="text-xs text-slate-500">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Subscription Plans */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6"
        >
          <h3 className="text-base font-bold text-white mb-3">Subscription-Specific Terms</h3>
          <div className="space-y-4">
            <div className="bg-slate-800/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-violet-400 mb-2">Monthly Plans</h4>
              <p className="text-xs text-slate-400">
                Full refund within 7 days. After 7 days, subscription continues until end of billing cycle. 
                You can cancel anytime to prevent future charges.
              </p>
            </div>
            <div className="bg-slate-800/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-amber-400 mb-2">6-Month & Annual Plans</h4>
              <p className="text-xs text-slate-400">
                Full refund within 7 days. After 7 days, pro-rated refund may be available for unused months 
                (minus 20% processing fee). Contact support for calculation.
              </p>
            </div>
            <div className="bg-slate-800/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-cyan-400 mb-2">Family Plans</h4>
              <p className="text-xs text-slate-400">
                Same terms as annual plans. Refunds apply to the entire family subscription, 
                not individual child accounts.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Important Notes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6"
        >
          <h3 className="text-base font-bold text-amber-400 mb-3 flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Important Notes
          </h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>Refunds are processed to the original payment method only.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>Bank processing times may vary (typically 5-10 business days after our approval).</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>In case of currency conversion, the refund amount may differ slightly due to exchange rates.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-400 mt-1">•</span>
              <span>Multiple refund requests may result in account review.</span>
            </li>
          </ul>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 border border-violet-500/30 rounded-2xl p-6 text-center"
        >
          <Mail className="w-8 h-8 text-violet-400 mx-auto mb-3" />
          <h3 className="text-base font-bold text-white mb-2">Need Help?</h3>
          <p className="text-sm text-slate-400 mb-3">
            For refund requests or questions about this policy:
          </p>
          <a 
            href="mailto:refunds@studyclaw.io" 
            className="text-violet-400 hover:text-violet-300 text-sm font-medium"
          >
            refunds@studyclaw.io
          </a>
          <p className="text-xs text-slate-600 mt-3">
            Please include your registered email and order ID in your request.
          </p>
        </motion.div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-600">
          This refund policy is effective as of February 1, 2026.
        </p>
      </div>
    </div>
  );
}
